import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Share2, Copy, Check, Users, KeyRound, ArrowRight, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";

export default function ShareParent() {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    api.get("/parent/share-info").then((r) => setShare(r.data)).finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/parent/share-link");
      setShare(r.data);
      toast.success("Share link generated");
    } catch { toast.error("Failed to generate link"); }
    finally { setLoading(false); }
  };

  const publicUrl = share?.token ? `${window.location.origin}/share/${share.token}` : null;

  const copy = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 1500);
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="share-parent-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Parent Portal</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Share with your parents</h1>
        <p className="text-slate-400 mt-2 max-w-xl">Two ways to give your parents visibility into your career journey.</p>

        {loading ? (
          <div className="mt-8 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : share?.token ? (
          <div className="mt-8 space-y-5">
            <div className="brimi-card p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold flex items-center gap-2"><LinkIcon className="w-4 h-4" /> Public share link (no login)</p>
              <p className="text-slate-600 text-sm mt-1">Send this link — anyone with it can view a read-only snapshot.</p>
              <div className="mt-3 flex gap-2">
                <input readOnly value={publicUrl} className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 outline-none bg-slate-50 text-xs" data-testid="share-link-input" />
                <button data-testid="share-link-copy" onClick={() => copy(publicUrl, setCopied)} className="brimi-pill accent-coral flex items-center gap-1 text-sm">
                  {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                </button>
              </div>
              <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="share-link-preview" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline">
                Preview <ArrowRight className="w-3 h-3" />
              </a>
            </div>

            <div className="brimi-card p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold flex items-center gap-2"><KeyRound className="w-4 h-4" /> Parent invite code (login required)</p>
              <p className="text-slate-600 text-sm mt-1">Share this code with your parent — they&apos;ll create their own account and link to you.</p>
              <div className="mt-3 flex gap-2 items-center">
                <div className="flex-1 border border-slate-200 rounded-xl px-3 py-2 bg-slate-50 font-display text-xl text-slate-900 tracking-widest" data-testid="share-invite-code">{share.invite_code}</div>
                <button data-testid="share-invite-copy" onClick={() => copy(share.invite_code, setCopiedCode)} className="brimi-pill accent-yellow flex items-center gap-1 text-sm">
                  {copiedCode ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Direct your parent to <Link to="/parent/login" className="font-bold text-blue-600">brimi.ai/parent/login</Link> and paste this code during signup.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-8 brimi-card p-10 text-center">
            <Users className="w-10 h-10 mx-auto text-yellow-500" />
            <p className="font-display text-2xl text-slate-900 mt-4">No share link yet</p>
            <p className="text-slate-500 mt-1">Generate a link + invite code to share your progress with parents.</p>
            <button data-testid="share-generate-btn" onClick={generate} className="brimi-pill accent-coral flex items-center gap-2 mx-auto mt-6">
              <Share2 className="w-4 h-4" /> Generate share link
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}
